/**
 * Razorpay payment service.
 * Loads the Razorpay checkout script on demand and opens the checkout modal.
 * V1: frontend-only — order is opened directly with the public key.
 * V2 (future backend): replace `createClientOptions` with a call to /api/orders/create
 *     that returns an `order_id`, and pass it via options.order_id for server-side verification.
 */

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

const loadScript = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no-window"));
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => reject(new Error("razorpay-load-failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("razorpay-load-failed"));
    document.body.appendChild(s);
  });

const createClientOptions = ({ amountInPaise, lead }) => ({
  key: process.env.REACT_APP_RAZORPAY_KEY_ID || "",
  amount: amountInPaise,
  currency: "INR",
  name: "BoostF",
  description: "eCommerce Launch Marathon — Seat Reservation",
  image: window.location.origin + "/logo.svg",
  prefill: {
    name: lead?.name || "",
    email: lead?.email || "",
    contact: lead?.phone || "",
  },
  notes: {
    product: "BoostF eCommerce Launch Marathon",
    leadId: lead?.id || "",
  },
  theme: {
    color: "#059669",
  },
});

export async function openRazorpayCheckout({ amountInr = 399, lead, onSuccess, onDismiss, onError }) {
  const keyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
  if (!keyId) {
    const err = new Error(
      "Razorpay key is not configured yet. Add REACT_APP_RAZORPAY_KEY_ID to your .env to enable payments."
    );
    onError?.(err);
    return { ok: false, reason: "no-key" };
  }

  try {
    await loadScript();
  } catch (e) {
    onError?.(e);
    return { ok: false, reason: "script-load-failed" };
  }

  const options = createClientOptions({
    amountInPaise: Math.round(amountInr * 100),
    lead,
  });

  const rzp = new window.Razorpay({
    ...options,
    handler: function (response) {
      onSuccess?.({
        paymentId: response.razorpay_payment_id,
        orderId: response.razorpay_order_id,
        signature: response.razorpay_signature,
      });
    },
    modal: {
  backdropclose: false,
  escape: false,
  confirm_close: true,
  animation: true,
  ondismiss: () => {
    onDismiss?.();
  },
},
retry: {
  enabled: true,
},
timeout: 300,
readonly: {
  email: false,
  contact: false,
},
  });
  rzp.on("payment.failed", function (response) {
    onError?.(response?.error || new Error("payment-failed"));
  });
  rzp.open();
  return { ok: true };
}

export const RAZORPAY_CONFIGURED = () => Boolean(process.env.REACT_APP_RAZORPAY_KEY_ID);
